import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Calendar, MapPin, Users, Euro, Trophy, Gamepad2, Download, ExternalLink } from 'lucide-react';
import { GAMES, getGameColor, getGameName } from '../constants/theme';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const formatDate = (d) => {
  if (!d) return '';
  return new Date(d).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

function AppDownloadBanner() {
  return (
    <div className="bg-gradient-to-r from-[#FF6B35]/10 to-[#4ECDC4]/10 border border-[#FF6B35]/20 rounded-xl p-5 mb-6" data-testid="app-download-banner">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h3 className="text-white font-semibold text-base mb-1">Téléchargez l'application pour une meilleure expérience</h3>
          <p className="text-[#B0B0B0] text-sm">Inscrivez-vous aux tournois, gérez vos parties et bien plus encore.</p>
        </div>
        <div className="flex items-center gap-3">
          <a href="https://play.google.com/store" target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#252525] border border-[#333] text-white text-sm font-medium hover:bg-[#333] transition-colors" data-testid="play-store-btn">
            <Download className="w-4 h-4" />Play Store
          </a>
          <a href="https://apps.apple.com" target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#252525] border border-[#333] text-white text-sm font-medium hover:bg-[#333] transition-colors" data-testid="app-store-btn">
            <Download className="w-4 h-4" />App Store
          </a>
          <Link to="/" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#FF6B35] text-white text-sm font-medium hover:bg-[#E55A25] transition-colors" data-testid="create-account-btn">
            Créer un compte
          </Link>
        </div>
      </div>
    </div>
  );
}

export function PublicTournamentDetail() {
  const { id } = useParams();
  const [tournament, setTournament] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API_URL}/api/public/tournaments/${id}`)
      .then(r => setTournament(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF6B35]"></div></div>;
  if (!tournament) return <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center text-white">Tournoi non trouvé</div>;

  const gc = getGameColor(tournament.game);
  const pct = tournament.capacity > 0 ? Math.round((tournament.participants_count / tournament.capacity) * 100) : 0;

  return (
    <div className="min-h-screen bg-[#0a0a0a] p-4 md:p-8" data-testid="public-tournament-detail">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <Link to="/events" className="text-[#666] hover:text-white transition-colors">
            <Trophy className="w-6 h-6 text-[#FF6B35]" />
          </Link>
          <span className="text-lg font-bold text-white"><span className="text-[#FF6B35]">Arcan</span>app</span>
        </div>

        <AppDownloadBanner />

        <Card className="bg-[#1a1a1a] border-[#333] overflow-hidden">
          <div className="h-1.5 w-full" style={{ backgroundColor: gc }} />
          <CardContent className="p-6 space-y-5">
            <div className="flex items-center gap-3 flex-wrap">
              <Badge style={{ backgroundColor: `${gc}20`, color: gc }} className="text-sm font-semibold gap-1">
                <Trophy className="w-3.5 h-3.5" />{getGameName(tournament.game)}
              </Badge>
              {tournament.format && <Badge className="bg-[#252525] text-[#B0B0B0]">{tournament.format}</Badge>}
              <Badge className={tournament.status === 'upcoming' ? 'bg-blue-500/20 text-blue-400' : 'bg-green-500/20 text-green-400'}>
                {tournament.status === 'upcoming' ? 'À venir' : 'En cours'}
              </Badge>
              {tournament.is_team && (
                <Badge className="bg-purple-500/15 text-purple-400">
                  <Users className="w-3 h-3 mr-1" />Équipe de {tournament.team_size || 2}
                </Badge>
              )}
            </div>

            <h1 className="text-2xl font-bold text-white">{tournament.name}</h1>
            {tournament.description && <p className="text-[#B0B0B0] whitespace-pre-line">{tournament.description}</p>}

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="flex items-center gap-2 text-[#B0B0B0]"><Calendar className="w-4 h-4 text-[#FF6B35]" /><span className="text-sm">{formatDate(tournament.date)}</span></div>
              {tournament.venue?.city && <div className="flex items-center gap-2 text-[#B0B0B0]"><MapPin className="w-4 h-4 text-[#4ECDC4]" /><span className="text-sm">{tournament.venue.name || tournament.venue.city}</span></div>}
              <div className="flex items-center gap-2 text-[#B0B0B0]"><Users className="w-4 h-4 text-[#7B68EE]" /><span className="text-sm">{tournament.participants_count}/{tournament.capacity}</span></div>
              <div className="flex items-center gap-2 text-[#B0B0B0]"><Euro className="w-4 h-4 text-[#FFCB05]" /><span className="text-sm">{tournament.price === 0 ? 'Gratuit' : `${tournament.price}€`}</span></div>
            </div>

            {/* Fill bar */}
            <div>
              <div className="flex items-center justify-between text-xs text-[#666] mb-1">
                <span>Remplissage</span><span>{pct}%</span>
              </div>
              <div className="h-2 rounded-full bg-[#252525] overflow-hidden">
                <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, backgroundColor: pct >= 100 ? '#EF4444' : pct > 75 ? '#F59E0B' : gc }} />
              </div>
            </div>

            {tournament.organizer && (
              <div className="flex items-center gap-3 pt-3 border-t border-[#333]">
                <div className="w-10 h-10 rounded-full bg-[#252525] flex items-center justify-center text-[#FF6B35] font-bold text-sm">
                  {tournament.organizer.name?.charAt(0) || '?'}
                </div>
                <div>
                  <p className="text-white text-sm font-medium">{tournament.organizer.company_name || tournament.organizer.name}</p>
                  <p className="text-[#666] text-xs">Organisateur</p>
                </div>
              </div>
            )}

            <div className="pt-3 border-t border-[#333]">
              <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-[#FF6B35] text-white font-medium hover:bg-[#E55A25] transition-colors" data-testid="signup-to-register">
                Créer un compte pour s'inscrire
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export function PublicEventsPage() {
  const [tournaments, setTournaments] = useState([]);
  const [availabilities, setAvailabilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [gameFilter, setGameFilter] = useState('');

  useEffect(() => {
    const params = gameFilter ? { game: gameFilter } : {};
    Promise.all([
      axios.get(`${API_URL}/api/public/tournaments`, { params }),
      axios.get(`${API_URL}/api/player-events`, { params }),
    ]).then(([t, a]) => {
      setTournaments(t.data);
      setAvailabilities(a.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [gameFilter]);

  return (
    <div className="min-h-screen bg-[#0a0a0a] p-4 md:p-8" data-testid="public-events-page">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Trophy className="w-7 h-7 text-[#FF6B35]" />
            <span className="text-xl font-bold text-white"><span className="text-[#FF6B35]">Arcan</span>app</span>
          </div>
          <Link to="/" className="px-4 py-2 rounded-lg bg-[#FF6B35] text-white text-sm font-medium hover:bg-[#E55A25] transition-colors" data-testid="login-btn-public">
            Connexion / Inscription
          </Link>
        </div>

        <AppDownloadBanner />

        {/* Game filter */}
        <div className="flex items-center gap-2 mb-6 flex-wrap">
          <button onClick={() => setGameFilter('')}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${!gameFilter ? 'bg-[#FF6B35] text-white' : 'bg-[#252525] text-[#B0B0B0] hover:bg-[#333]'}`}
            data-testid="filter-all">Tous</button>
          {GAMES.map(g => (
            <button key={g.id} onClick={() => setGameFilter(g.id)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${gameFilter === g.id ? 'text-white' : 'text-[#B0B0B0] hover:bg-[#333]'}`}
              style={gameFilter === g.id ? { backgroundColor: g.color } : { backgroundColor: '#252525' }}
              data-testid={`filter-${g.id}`}>{g.shortName}</button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF6B35]"></div></div>
        ) : (
          <>
            {/* Tournaments */}
            <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2"><Trophy className="w-5 h-5 text-[#FF6B35]" />Tournois à venir ({tournaments.length})</h2>
            <div className="grid gap-3 mb-8">
              {tournaments.map(t => (
                <Link key={t.id} to={`/events/tournament/${t.id}`} className="block">
                  <Card className="bg-[#1a1a1a] border-[#333] hover:border-[#FF6B35]/50 transition-all">
                    <CardContent className="p-0">
                      <div className="flex">
                        <div className="w-1 rounded-l-lg" style={{ backgroundColor: getGameColor(t.game) }} />
                        <div className="flex-1 p-4">
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <Badge style={{ backgroundColor: `${getGameColor(t.game)}20`, color: getGameColor(t.game) }} className="text-xs">{getGameName(t.game)}</Badge>
                            {t.format && <Badge className="bg-[#252525] text-[#B0B0B0] text-xs">{t.format}</Badge>}
                          </div>
                          <h3 className="text-white font-semibold mb-2">{t.name}</h3>
                          <div className="flex items-center gap-4 text-sm text-[#666] flex-wrap">
                            <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{new Date(t.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                            {t.venue?.city && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{t.venue.city}</span>}
                            <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" />{t.participants_count}/{t.capacity}</span>
                            <span className="font-semibold" style={{ color: t.price === 0 ? '#4CAF50' : '#FF6B35' }}>{t.price === 0 ? 'Gratuit' : `${t.price}€`}</span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
              {tournaments.length === 0 && <p className="text-[#666] text-center py-8">Aucun tournoi à venir</p>}
            </div>

            {/* Availabilities */}
            <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2"><Gamepad2 className="w-5 h-5 text-[#4ECDC4]" />Parties à venir ({availabilities.length})</h2>
            <div className="grid gap-3">
              {availabilities.map(a => (
                <Link key={a.id} to={`/game/${a.id}`} className="block">
                  <Card className="bg-[#1a1a1a] border-[#333] hover:border-[#4ECDC4]/50 transition-all">
                    <CardContent className="p-0">
                      <div className="flex">
                        <div className="w-1 rounded-l-lg" style={{ backgroundColor: getGameColor(a.game) }} />
                        <div className="flex-1 p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge style={{ backgroundColor: `${getGameColor(a.game)}20`, color: getGameColor(a.game) }} className="text-xs">{getGameName(a.game)}</Badge>
                            <Badge className="bg-[#7B68EE]/20 text-[#7B68EE] text-xs">Partie</Badge>
                          </div>
                          <h3 className="text-white font-semibold mb-2">{a.description || 'Partie sans nom'}</h3>
                          <div className="flex items-center gap-4 text-sm text-[#666] flex-wrap">
                            <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{new Date(a.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                            {a.location?.city && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{a.location.city}</span>}
                            <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" />{a.participants_count}/{a.max_players}</span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
              {availabilities.length === 0 && <p className="text-[#666] text-center py-8">Aucune partie à venir</p>}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
